import React, { useEffect, useState } from 'react';
import { Download, Share, X, Plus } from 'lucide-react';

// Banner "Instalar app" — SOLO móvil (sm:hidden), descartable por 14 días.
// - Chrome/Android: usa el evento beforeinstallprompt (instalación de 1 toque).
// - iOS/Safari: no hay API → mostramos el hint "Compartir → Agregar a inicio".
// No maneja notificaciones (decisión de producto: el panel no usa push).

const DISMISS_KEY = 'lealify-install-dismissed';
const DISMISS_DAYS = 14;

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS
    (window.navigator as any).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function recentlyDismissed(): boolean {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || '0');
    return ts > 0 && Date.now() - ts < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
}

const InstallPrompt: React.FC = () => {
  const [deferred, setDeferred] = useState<any>(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android/Chrome: el navegador nos cede el prompt nativo.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS no dispara el evento → mostramos el hint manual.
    if (isIos()) {
      setIosHint(true);
      setShow(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="sm:hidden fixed inset-x-0 bottom-0 z-[35] px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-line bg-surface shadow-xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-accent/15 flex items-center justify-center">
            <Download className="h-5 w-5 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-content">Instala Lealify en tu inicio</p>
            {iosHint ? (
              <p className="mt-1 text-xs text-content-3 leading-relaxed">
                Toca <Share className="inline h-3.5 w-3.5 align-text-bottom" /> Compartir y luego{' '}
                <span className="font-medium text-content-2">
                  <Plus className="inline h-3.5 w-3.5 align-text-bottom" /> Agregar a inicio
                </span>
                .
              </p>
            ) : (
              <p className="mt-1 text-xs text-content-3">Ábrelo como una app, sin abrir el navegador.</p>
            )}
            {!iosHint && (
              <button
                onClick={install}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-solid/90 transition-colors"
              >
                <Download className="h-4 w-4" />
                Instalar
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            className="flex-shrink-0 -mt-1 -mr-1 p-1.5 rounded-full text-content-3 hover:bg-surface-2"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
